import { GraphQLError } from "graphql"

import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { getStoreKeysWithChannels } from "@/graphql/schemas/shared/utils/getStoreKeysWithChannels"
import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getInsertActions, getTwoKProducts, updateCustomerCart } from "../utils/cartHelpers"
import { getActiveCustomerCart } from "../utils/getCart"
import { getItemsWithSelections } from "../utils/getItemsWithSelections"

export const mutationAddCustomLineItems = {
  async addCustomLineItems(parent, args, context, info) {
    let { customerId, items = [], lang, cdn } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.CARTS,
      operation: Operations.UPDATE,
    })

    const commerceApi = CommerceService.api()

    const customerCarts = await getActiveCustomerCart(customerId, user.employeeNumber)
    const customerCart = customerCarts[0]
    // custom type created for custom line custom fields
    const customTypeLineItem = (
      await commerceApi.types().withKey({ key: "customCustomLineItemTypeKey" }).get().execute()
    )?.body

    if (!customerCart || customerCart.cartState !== "Active") {
      throw new Error("Cart was not found or is not in active state")
    }

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const { customerId: idOfAcustomer } = customerCart
    const customer = await getCustomer({ id: idOfAcustomer })

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    const { stores: customerStores, customerGroup } = customer
    const storeKeysWithChannels = await getStoreKeysWithChannels(customerStores)

    const totalItems = customerCart?.customLineItems?.length || 0
    let itemsWithSelections = await getItemsWithSelections(items, totalItems)

    const twoKProducts = await getTwoKProducts(itemsWithSelections, client)

    if (itemsWithSelections.length > 0 && twoKProducts.length > 0) {
      itemsWithSelections = [...itemsWithSelections, ...twoKProducts]
    }

    if (itemsWithSelections.length < 1) throw new Error("Nothing to add!")

    const totalProjectedItems = Number(itemsWithSelections.length) + Number(customerCart?.customLineItems?.length || 0)
    if (totalProjectedItems > 200) {
      throw new GraphQLError("TOO_MANY_ITEMS", {
        extensions: {
          code: "TOO_MANY_ITEMS",
        },
      })
    }

    const actions = await getInsertActions({
      itemsWithSelections,
      customerCart,
      storeKeysWithChannels,
      customerGroup,
      currencyCode: customerCart.totalPrice.currencyCode,
      customTypeLineItem,
    })

    if (actions.length < 1) {
      throw new Error("Nothing to add!")
    }

    const updatedCart = await updateCustomerCart({ customerCart, actions })

    return updatedCart
  },
}
